import React, { useRef, useEffect } from "react";
import classNames from "classnames";
import { GRADE_SCALE, STORAGE_GRADE_SCALE, GRADE_COLORS } from "utils/enclosure/embodiedCarbon";
import Tooltip from "components/Tooltip";
import classes from "./style.module.scss";

/** When kg/m² is zero or non-finite, show no filled segments (aligns with insulation “None” and map N/A rows). */
const gradeForBar = (metricKgPerM2, letterGrade) => {
    if (letterGrade == null || letterGrade === "N/A") return "N/A";
    if (typeof metricKgPerM2 === "number") {
        if (!Number.isFinite(metricKgPerM2)) return "N/A";
        if (metricKgPerM2 === 0) return "N/A";
    }
    return letterGrade;
};

const GradeBar = ({ grade, type = "emission" }) => {
    const gradeScale = type === "storage" ? STORAGE_GRADE_SCALE : GRADE_SCALE;
    const gradeIndex = gradeScale.findIndex(({ label }) => label === grade);
    const prevIndexRef = useRef(gradeIndex);

    const goingBackward = gradeIndex < prevIndexRef.current;

    useEffect(() => {
        prevIndexRef.current = gradeIndex;
    }, [gradeIndex]);

    return (
        <div className={classes.gradeBar}>
            {gradeScale.map(({ label }, i) => {
                const isActive = gradeIndex >= 0 && i <= gradeIndex;
                const delay = goingBackward ? (gradeScale.length - 1 - i) * 0.05 : i * 0.05;
                return (
                    <div
                        key={label}
                        className={`${classes.gradeSegment} ${isActive ? classes.active : ""}`}
                        style={{
                            backgroundColor: type === "storage" ? GRADE_COLORS["A+"] : GRADE_COLORS[label],
                            transitionDelay: `${delay}s`,
                        }}
                    />
                );
            })}
        </div>
    );
};

/** Format kg/m² for the strip. Missing values use em dash; non-finite numbers render as 0.00 (legacy safety). */
export const formatEmbodiedMetric = (v) => {
    if (v == null) return "—";
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isNaN(n) || !Number.isFinite(n)) return (0).toFixed(2);
    return n.toFixed(2);
};

const GradeDisplay = ({
    emissions,
    storage,
    emissionGrade,
    storageGrade,
    withBorder = false,
    borderHeight = "100%",
    borderColor = "#E0E0E0",
    customGap = "0.75rem",
    customPadding = "0 7px",
    hasMismatch = false,
    /** Use with framing / whole-window rows: avoids `height: 100%` stretching that shifts numbers vertically. */
    layout = "default",
}) => {
    const rootClass =
        layout === "framingGroup"
            ? `${classes.gradeDisplay} ${classes.gradeDisplayFramingGroup}`
            : classes.gradeDisplay;

    /* Inline `padding` shorthand (e.g. "0 7px") zeros out padding-top and overrides the module class — set top explicitly after. */
    const rootStyle =
        layout === "framingGroup"
            ? { gap: customGap, padding: customPadding, paddingTop: 6, paddingBottom: 4 }
            : { gap: customGap, padding: customPadding };

    return (
        <div className={classNames(rootClass, { [classes.mismatch]: hasMismatch })} style={rootStyle}>
            <div className={classes.gradeColumn} data-tip="Embodied Emissions" data-for="embodiedCarbonEmissionsTip">
                <span className={classes.gradeValue}>
                    {formatEmbodiedMetric(emissions)}{" "}
                    <span className={classes.gradeUnit}>
                        kgCO<sub>2</sub>/m<sup>2</sup>
                    </span>
                </span>
                <GradeBar grade={gradeForBar(emissions, emissionGrade)} />
            </div>
            {withBorder && <div style={{ width: "1px", height: borderHeight, backgroundColor: borderColor }} />}
            <div className={classes.gradeColumn} data-tip="Storage" data-for="embodiedCarbonStorageTip">
                <span className={classes.gradeValue}>
                    {formatEmbodiedMetric(storage)}{" "}
                    <span className={classes.gradeUnit}>
                        kgCO<sub>2</sub>/m<sup>2</sup>
                    </span>
                </span>
                <GradeBar grade={gradeForBar(storage, storageGrade)} type="storage" />
            </div>
            <Tooltip id="embodiedCarbonEmissionsTip" />
            <Tooltip id="embodiedCarbonStorageTip" />
        </div>
    );
};

export default GradeDisplay;
